/**
 * RECEPTION-FIX-AND-POLISH-BATCH-V1 -- THE SHARED ENTERPRISE SHELL
 * CONTRACT (STEP 0; UX LAW 3: ONE VISUAL LANGUAGE).
 *
 * The page-rhythm + density contract every role console inherits
 * (Reception first; Doctor/Owner/Manager/COC batches consume the same
 * shell). This module is the TS half of the contract: the class-name
 * vocabulary, the density-token names (declared in dashboard.css, values
 * there -- ONE home), and the measurable ONE-PRIMARY-ACTION rule. It
 * invents NO new visual vocabulary: everything composes the frozen canon
 * (design-language 02/03 + 07 DENSITY).
 *
 * THE SHELL, in five statements:
 *  1. PAGE RHYTHM: a console page is `.ac-page` -- a single vertical
 *     stack with `--ac-page-gap` between sections; sections are the
 *     existing SectionBand / SectionShell / plain regions, never ad-hoc
 *     margins.
 *  2. SUMMARY FIRST (UX LAW 4): the page opens with SummaryHeader --
 *     PageHeader chrome + a KpiTile strip; details below.
 *  3. NUMBERS FIRST (UX LAW 2): headline figures are hero-size KpiTiles;
 *     tables render UNDER the numbers, denser (row pad rides
 *     `--ac-row-pad-y`).
 *  4. ONE PRIMARY ACTION (DENSITY rule 8 / S-2): at most ONE
 *     `ac-btn--primary` outside table rows/drawers per screen state --
 *     measurable via countPrimaryActions below; per-row primaries in a
 *     queue table are row-scoped and excluded by construction (they
 *     carry `ac-btn--sm`).
 *  5. HONEST STATES: empty/error render through EmptyState/ErrorState
 *     inside their section -- one vocabulary, no stacked empty-card
 *     walls; an unproven number is '-' plus a named truth, never 0.
 *
 * DENSITY TOKENS (the DENSITY GATE contract -- names here, values in
 * dashboard.css :root, ONE home): --ac-page-gap (section separation),
 * --ac-strip-gap (tile-strip gap), --ac-row-pad-y (table/list row
 * density, the declared axis), --ac-space-* (the ratified scale),
 * --ac-text-metric / --ac-text-metric-lg (number grades).
 */

/** The shell page class -- consumers compose `ac-page` + their own page
 *  namespace class (e.g. `today-home`). */
export const SHELL_PAGE_CLASS = 'ac-page';

/** The density-token names the shell contract pins (values live in
 *  dashboard.css -- ONE home; this list is the DENSITY GATE's checklist). */
export const SHELL_DENSITY_TOKENS = Object.freeze([
  '--ac-page-gap',
  '--ac-strip-gap',
  '--ac-row-pad-y',
  '--ac-text-metric',
  '--ac-text-metric-lg',
] as const);

/**
 * THE ONE-PRIMARY-ACTION RULE, measurable. Counts page-level primary
 * actions in rendered markup: `ac-btn--primary` occurrences EXCLUDING
 * row-scoped small primaries (`ac-btn--primary ac-btn--sm` -- queue-row
 * doors, which are per-row by law and capped by the row's own state
 * machine). Tests assert `<= 1` per screen state.
 */
export function countPrimaryActions(html: string): number {
  const all = html.match(/ac-btn--primary(?![\w-])/g)?.length ?? 0;
  const rowScoped =
    html.match(/ac-btn--primary ac-btn--sm(?![\w-])/g)?.length ?? 0;
  const rowScopedReversed =
    html.match(/ac-btn--sm ac-btn--primary(?![\w-])/g)?.length ?? 0;
  return all - rowScoped - rowScopedReversed;
}
